import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  BarChart3, RefreshCw, Filter,
} from "lucide-react";
import { useApp } from "@/context/AppContext";
import {
  pvStatuses, pvReportAgeing, pvReportDailySummary,
  pvReportEddCompliance, pvReportStatusWise, pvReportServicePerformance,
} from "@/lib/api";

const REPORT_TABS = [
  { key: "ageing",       label: "PV Ageing" },
  { key: "daily",        label: "Daily PV Summary" },
  { key: "edd",          label: "EDD Compliance" },
  { key: "status",       label: "Status-Wise" },
  { key: "service_perf", label: "Service Performance" },
];

const SERVICE_OPTIONS = ["All", "General Service", "Body Repair", "Mechanical Repair", "Warranty Service", "Accident Repair"];

const todayStr = () => new Date().toISOString().slice(0, 10);
const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  const s = String(iso).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const [y, m, d] = s.split("-");
  return `${d}/${m}/${y}`;
};

const PvReports = () => {
  const { currentUser, locations } = useApp();
  const allowedLocs = useMemo(
    () => locations.filter(
      (l) => currentUser?.role === "admin" || currentUser?.role === "crm" ||
             (currentUser?.location_codes || []).includes(l.code),
    ),
    [locations, currentUser],
  );

  const [tab, setTab] = useState("ageing");
  const [locCode, setLocCode] = useState("");
  const [statusId, setStatusId] = useState("");
  const [service, setService] = useState("All");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [singleDate, setSingleDate] = useState(todayStr());
  const [month, setMonth] = useState(currentMonth());
  const [statusOptions, setStatusOptions] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadStatuses = useCallback(async () => {
    try { const d = await pvStatuses(false); setStatusOptions(d.rows || []); }
    catch {/* silent */}
  }, []);
  useEffect(() => { loadStatuses(); }, [loadStatuses]);

  const load = useCallback(async () => {
    setLoading(true); setData(null);
    try {
      const commonParams = { location_code: locCode || undefined };
      if (tab === "ageing") {
        const r = await pvReportAgeing({
          ...commonParams,
          pv_status_id: statusId || undefined,
          service_type: service !== "All" ? service : undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
        });
        setData(r);
      } else if (tab === "daily") {
        setData(await pvReportDailySummary({ ...commonParams, date: singleDate || undefined }));
      } else if (tab === "edd") {
        setData(await pvReportEddCompliance({ ...commonParams, date_from: dateFrom || undefined, date_to: dateTo || undefined }));
      } else if (tab === "status") {
        setData(await pvReportStatusWise({ ...commonParams, month: month || undefined }));
      } else if (tab === "service_perf") {
        setData(await pvReportServicePerformance({ ...commonParams, month: month || undefined }));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load report");
    } finally { setLoading(false); }
  }, [tab, locCode, statusId, service, dateFrom, dateTo, singleDate, month]);

  useEffect(() => { load(); }, [load]);

  const clear = () => {
    setLocCode(""); setStatusId(""); setService("All");
    setDateFrom(""); setDateTo(""); setSingleDate(todayStr()); setMonth(currentMonth());
  };

  return (
    <div className="max-w-[1400px] mx-auto space-y-4" data-testid="pv-reports-page">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-lg bg-yamaha-50 flex items-center justify-center">
          <BarChart3 className="w-6 h-6 text-yamaha-700" />
        </div>
        <div className="flex-1">
          <h1 className="text-[22px] font-semibold text-yamaha-800 leading-none tracking-tight">
            Pending Vehicle Reports
          </h1>
          <p className="text-[12.5px] text-[#747684] mt-1">
            All reports are location-first. Filters respect your assigned locations.
          </p>
        </div>
        <button onClick={load} data-testid="pv-rep-refresh"
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#e2e1eb] text-[12px] text-[#444653] hover:bg-white">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-[#e2e1eb] flex items-center gap-1 overflow-x-auto">
        {REPORT_TABS.map((t) => (
          <button key={t.key} onClick={() => { setTab(t.key); setData(null); }} data-testid={`pv-rep-tab-${t.key}`}
            className={`px-3 py-2 text-[12.5px] font-semibold transition-colors border-b-2 -mb-px whitespace-nowrap ${
              tab === t.key ? "text-yamaha-700 border-yamaha-700" : "text-[#747684] border-transparent hover:text-[#1a1b22]"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#e2e1eb] rounded-xl p-3 flex flex-wrap items-center gap-2">
        <Filter className="w-3.5 h-3.5 text-[#747684]" />
        <select data-testid="pv-rep-loc" value={locCode} onChange={(e) => setLocCode(e.target.value)}
          className="border border-[#e2e1eb] rounded-md px-2 py-1.5 text-[12px]">
          <option value="">All Locations</option>
          {allowedLocs.map((l) => <option key={l.code} value={l.code}>{l.name} ({l.code})</option>)}
        </select>
        {(tab === "ageing") && (
          <select data-testid="pv-rep-status" value={statusId} onChange={(e) => setStatusId(e.target.value)}
            className="border border-[#e2e1eb] rounded-md px-2 py-1.5 text-[12px]">
            <option value="">All PV Statuses</option>
            {statusOptions.map((s) => <option key={s.id} value={s.id}>{s.status_name}</option>)}
          </select>
        )}
        {(tab === "ageing") && (
          <select data-testid="pv-rep-service" value={service} onChange={(e) => setService(e.target.value)}
            className="border border-[#e2e1eb] rounded-md px-2 py-1.5 text-[12px]">
            {SERVICE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
        {(tab === "ageing" || tab === "edd") && (
          <>
            <span className="text-[11px] text-[#747684]">JC Date</span>
            <input type="date" data-testid="pv-rep-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className="border border-[#e2e1eb] rounded-md px-2 py-1.5 text-[12px]" />
            <span className="text-[11px] text-[#747684]">to</span>
            <input type="date" data-testid="pv-rep-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              className="border border-[#e2e1eb] rounded-md px-2 py-1.5 text-[12px]" />
          </>
        )}
        {tab === "daily" && (
          <input type="date" data-testid="pv-rep-date" value={singleDate} onChange={(e) => setSingleDate(e.target.value)}
            className="border border-[#e2e1eb] rounded-md px-2 py-1.5 text-[12px]" />
        )}
        {(tab === "status" || tab === "service_perf") && (
          <input type="month" data-testid="pv-rep-month" value={month} onChange={(e) => setMonth(e.target.value)}
            className="border border-[#e2e1eb] rounded-md px-2 py-1.5 text-[12px]" />
        )}
        <button onClick={load} data-testid="pv-rep-apply"
          className="px-3 py-1.5 rounded-md bg-yamaha-700 hover:bg-yamaha-800 text-white text-[11.5px] font-semibold">Apply</button>
        <button onClick={clear} data-testid="pv-rep-clear"
          className="px-3 py-1.5 rounded-md border border-[#e2e1eb] text-[11.5px] font-semibold text-[#444653] hover:bg-[#faf8ff]">Clear</button>
      </div>

      {/* Body */}
      {loading && <div className="bg-white border border-[#e2e1eb] rounded-xl py-10 text-center text-[13px] text-[#747684]">Loading report…</div>}
      {!loading && data && tab === "ageing"       && data.rows          !== undefined && <AgeingReport data={data} />}
      {!loading && data && tab === "daily"        && data.totals        !== undefined && <DailyReport data={data} />}
      {!loading && data && tab === "edd"          && data.detail        !== undefined && <EddReport data={data} />}
      {!loading && data && tab === "status"       && data.service_type  !== undefined && <StatusReport data={data} />}
      {!loading && data && tab === "service_perf" && data.month         !== undefined && <ServicePerfReport data={data} />}
    </div>
  );
};

// ============= Individual reports =============
const AgeingReport = ({ data }) => (
  <div className="bg-white border border-[#e2e1eb] rounded-xl overflow-hidden">
    <div className="px-3 py-2 flex items-center justify-between border-b border-[#e2e1eb]">
      <div className="text-[12px] font-semibold text-[#444653]">PV Ageing Report</div>
      <div className="text-[11.5px] text-[#747684]">{data.total} record{data.total === 1 ? "" : "s"}</div>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-[12px]" data-testid="pv-rep-ageing-table">
        <thead className="bg-[#faf8ff] text-[10.5px] text-[#747684] uppercase tracking-wider sticky top-0 z-10">
          <tr>
            <th className="px-3 py-2 text-left">Location</th>
            <th className="px-3 py-2 text-left">JC No</th>
            <th className="px-3 py-2 text-left">JC Date</th>
            <th className="px-3 py-2 text-left">Customer</th>
            <th className="px-3 py-2 text-left">Vehicle</th>
            <th className="px-3 py-2 text-left">Reg #</th>
            <th className="px-3 py-2 text-left">PV Status</th>
            <th className="px-3 py-2 text-left">Service Type</th>
            <th className="px-3 py-2 text-left">EDD</th>
            <th className="px-3 py-2 text-center">Pending Days</th>
            <th className="px-3 py-2 text-left">Remark</th>
            <th className="px-3 py-2 text-left">Updated By</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#eeecf6]">
          {data.rows.length === 0 && (
            <tr><td colSpan={12} className="text-center text-[12.5px] text-[#747684] py-8">No records for the selected filters.</td></tr>
          )}
          {data.rows.map((r, i) => (
            <tr key={i} className="hover:bg-[#faf8ff]">
              <td className="px-3 py-2 font-mono">{r.location_code}</td>
              <td className="px-3 py-2 font-mono font-semibold text-yamaha-700">{r.job_card_no}</td>
              <td className="px-3 py-2">{fmtDate(r.job_card_date)}</td>
              <td className="px-3 py-2">{r.customer_name || "—"}</td>
              <td className="px-3 py-2">{r.vehicle_model || "—"}</td>
              <td className="px-3 py-2 font-mono">{r.registration_number || "—"}</td>
              <td className="px-3 py-2">{r.pv_status || "—"}</td>
              <td className="px-3 py-2">{r.service_type || "—"}</td>
              <td className="px-3 py-2">{fmtDate(r.expected_delivery_date)}</td>
              <td className="px-3 py-2 text-center">
                <span className={`inline-block px-2 py-0.5 rounded-full text-[10.5px] font-semibold border ${
                  (r.pending_days ?? 0) > 7 ? "bg-rose-50 text-rose-700 border-rose-200" :
                  (r.pending_days ?? 0) > 3 ? "bg-amber-50 text-amber-700 border-amber-200" :
                  "bg-emerald-50 text-emerald-700 border-emerald-200"
                }`}>{r.pending_days ?? "—"} d</span>
              </td>
              <td className="px-3 py-2 max-w-[220px] truncate">{r.latest_remark || "—"}</td>
              <td className="px-3 py-2 text-[#747684]">{r.last_updated_by || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const DailyReport = ({ data }) => (
  <div className="bg-white border border-[#e2e1eb] rounded-xl overflow-hidden">
    <div className="px-3 py-2 flex items-center justify-between border-b border-[#e2e1eb]">
      <div className="text-[12px] font-semibold text-[#444653]">Daily PV Summary · {fmtDate(data.date)}</div>
      <div className="text-[11.5px] text-[#747684]">{data.rows.length} location{data.rows.length === 1 ? "" : "s"}</div>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-[12px]" data-testid="pv-rep-daily-table">
        <thead className="bg-[#faf8ff] text-[10.5px] text-[#747684] uppercase tracking-wider sticky top-0 z-10">
          <tr>
            <th className="px-3 py-2 text-left">Location</th>
            <th className="px-3 py-2 text-center">Opening Pending</th>
            <th className="px-3 py-2 text-center">New Pending</th>
            <th className="px-3 py-2 text-center">Work Completed</th>
            <th className="px-3 py-2 text-center">Delivered</th>
            <th className="px-3 py-2 text-center">Cancelled</th>
            <th className="px-3 py-2 text-center">Closing Pending</th>
            <th className="px-3 py-2 text-center">Monthly Total</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#eeecf6]">
          {data.rows.length === 0 && (
            <tr><td colSpan={8} className="text-center text-[12.5px] text-[#747684] py-8">No activity for the selected date.</td></tr>
          )}
          {data.rows.map((r) => (
            <tr key={r.location_code} className="hover:bg-[#faf8ff]">
              <td className="px-3 py-2 font-mono font-semibold">{r.location_code}</td>
              <td className="px-3 py-2 text-center">{r.opening_pending}</td>
              <td className="px-3 py-2 text-center">{r.new_pending}</td>
              <td className="px-3 py-2 text-center">{r.work_completed}</td>
              <td className="px-3 py-2 text-center text-emerald-700 font-semibold">{r.delivered}</td>
              <td className="px-3 py-2 text-center text-rose-700">{r.cancelled}</td>
              <td className="px-3 py-2 text-center font-semibold">{r.closing_pending}</td>
              <td className="px-3 py-2 text-center text-[#444653]">{r.monthly_total_pending}</td>
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-[#faf8ff] font-semibold">
          <tr>
            <td className="px-3 py-2">Total</td>
            <td className="px-3 py-2 text-center">{data.totals.opening_pending}</td>
            <td className="px-3 py-2 text-center">{data.totals.new_pending}</td>
            <td className="px-3 py-2 text-center">{data.totals.work_completed}</td>
            <td className="px-3 py-2 text-center text-emerald-700">{data.totals.delivered}</td>
            <td className="px-3 py-2 text-center text-rose-700">{data.totals.cancelled}</td>
            <td className="px-3 py-2 text-center">{data.totals.closing_pending}</td>
            <td className="px-3 py-2 text-center">{data.totals.monthly_total_pending}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
);

const EddReport = ({ data }) => (
  <div className="bg-white border border-[#e2e1eb] rounded-xl overflow-hidden">
    <div className="px-3 py-2 border-b border-[#e2e1eb] text-[12px] font-semibold text-[#444653]">EDD Compliance</div>
    <div className="overflow-x-auto">
      <table className="w-full text-[12px]" data-testid="pv-rep-edd-table">
        <thead className="bg-[#faf8ff] text-[10.5px] text-[#747684] uppercase tracking-wider sticky top-0 z-10">
          <tr>
            <th className="px-3 py-2 text-left">Location</th>
            <th className="px-3 py-2 text-center">With EDD</th>
            <th className="px-3 py-2 text-center">Completed Within EDD</th>
            <th className="px-3 py-2 text-center">Completed After EDD</th>
            <th className="px-3 py-2 text-center">Missed &amp; Pending</th>
            <th className="px-3 py-2 text-center">No EDD</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#eeecf6]">
          {data.rows.length === 0 && (
            <tr><td colSpan={6} className="text-center text-[12.5px] text-[#747684] py-8">No data for the selected filters.</td></tr>
          )}
          {data.rows.map((r) => (
            <tr key={r.location_code}>
              <td className="px-3 py-2 font-mono font-semibold">{r.location_code}</td>
              <td className="px-3 py-2 text-center">{r.with_edd}</td>
              <td className="px-3 py-2 text-center text-emerald-700 font-semibold">{r.completed_within_edd}</td>
              <td className="px-3 py-2 text-center text-amber-700">{r.completed_after_edd}</td>
              <td className="px-3 py-2 text-center text-rose-700 font-semibold">{r.missed_still_pending}</td>
              <td className="px-3 py-2 text-center text-[#747684]">{r.no_edd_updated}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <div className="px-3 py-2 border-t border-[#e2e1eb] text-[11.5px] text-[#747684]">
      Detail: {data.detail.length} record{data.detail.length === 1 ? "" : "s"} within scope
    </div>
  </div>
);

const StatusReport = ({ data }) => (
  <div className="space-y-4">
    <div className="bg-white border border-[#e2e1eb] rounded-xl overflow-hidden">
      <div className="px-3 py-2 border-b border-[#e2e1eb] text-[12px] font-semibold text-[#444653]">Status-Wise by Location</div>
      <div className="p-3 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="pv-rep-status-list">
        {data.rows.length === 0 && (
          <div className="text-center text-[12.5px] text-[#747684] col-span-2 py-8">No Active Pending records for the selected filters.</div>
        )}
        {data.rows.map((r) => (
          <div key={r.location_code} className="border border-[#e2e1eb] rounded-lg overflow-hidden">
            <div className="px-3 py-1.5 bg-[#faf8ff] text-[11.5px] font-semibold text-yamaha-700">{r.location_code}</div>
            <div className="divide-y divide-[#eeecf6]">
              {r.statuses.map((s) => (
                <div key={s.status_name} className="flex items-center justify-between px-3 py-1.5 text-[12.5px]">
                  <span className="text-[#1a1b22]">{s.status_name}</span>
                  <span className="font-mono font-semibold text-[#444653]">{s.count}</span>
                </div>
              ))}
              {r.statuses.length === 0 && <div className="px-3 py-2 text-[12px] text-[#747684]">No active pending.</div>}
            </div>
          </div>
        ))}
      </div>
    </div>

    <div className="bg-white border border-[#e2e1eb] rounded-xl overflow-hidden">
      <div className="px-3 py-2 border-b border-[#e2e1eb] text-[12px] font-semibold text-[#444653]">Service Type · Avg Turnaround</div>
      <table className="w-full text-[12px]">
        <thead className="bg-[#faf8ff] text-[10.5px] text-[#747684] uppercase tracking-wider sticky top-0 z-10">
          <tr>
            <th className="px-3 py-2 text-left">Service Type</th>
            <th className="px-3 py-2 text-center">Avg Turnaround (days)</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#eeecf6]">
          {data.service_type.length === 0 && (
            <tr><td colSpan={2} className="text-center text-[12.5px] text-[#747684] py-4">No turnaround data yet this month.</td></tr>
          )}
          {data.service_type.map((r) => (
            <tr key={r.service_type}>
              <td className="px-3 py-2 text-[#1a1b22]">{r.service_type}</td>
              <td className="px-3 py-2 text-center">{r.avg_turnaround_days ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const ServicePerfReport = ({ data }) => (
  <div className="bg-white border border-[#e2e1eb] rounded-xl overflow-hidden">
    <div className="px-3 py-2 border-b border-[#e2e1eb] flex items-center justify-between">
      <div className="text-[12px] font-semibold text-[#444653]">Service Performance</div>
      <div className="text-[11.5px] text-[#747684]">Month {data.month}</div>
    </div>
    <table className="w-full text-[12px]" data-testid="pv-rep-service-perf-table">
      <thead className="bg-[#faf8ff] text-[10.5px] text-[#747684] uppercase tracking-wider sticky top-0 z-10">
        <tr>
          <th className="px-3 py-2 text-left">Service Type</th>
          <th className="px-3 py-2 text-center">Monthly Pending</th>
          <th className="px-3 py-2 text-center">Avg Turnaround (days)</th>
          <th className="px-3 py-2 text-center">EDD Compliance %</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-[#eeecf6]">
        {data.rows.length === 0 && (
          <tr><td colSpan={4} className="text-center text-[12.5px] text-[#747684] py-8">No service-type activity this month.</td></tr>
        )}
        {data.rows.map((r) => (
          <tr key={r.service_type}>
            <td className="px-3 py-2 text-[#1a1b22]">{r.service_type}</td>
            <td className="px-3 py-2 text-center font-semibold text-[#444653]">{r.monthly_pending}</td>
            <td className="px-3 py-2 text-center">{r.avg_turnaround_days ?? "—"}</td>
            <td className="px-3 py-2 text-center">
              {r.edd_compliance_pct == null ? "—" : (
                <span className={`inline-block px-2 py-0.5 rounded-full text-[10.5px] font-semibold border ${
                  r.edd_compliance_pct >= 80 ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : r.edd_compliance_pct >= 50 ? "bg-amber-50 text-amber-700 border-amber-200"
                  : "bg-rose-50 text-rose-700 border-rose-200"
                }`}>{r.edd_compliance_pct}%</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default PvReports;
